import {isNil} from 'ramda';

import {appWorkspaceKey} from '@dms/config';

import {Nullish} from 'shared/web';

const WORKSPACE_LABEL_PATTERN = /^[a-z0-9-]+$/i;
const API_HOST_FIRST_LABEL = 'api';

/**
 * Routes API requests to the workspace-labelled host (`<workspace>.api.<env>`) by
 * prepending the workspace to the URL hostname. Replaces the X-Workspace header.
 *
 * The url is returned unchanged when:
 * - the workspace is nil, empty, or not a valid host label (prevents an invalid hostname),
 * - the workspace is the reserved `app` redirect pseudo-workspace (never a tenant),
 * - the hostname is already workspace-prefixed (interceptors may re-run on a mutated config),
 * - the hostname's first label is not `api` (the backend resolver only parses `<wsp>.api.*`,
 *   so prefixing localhost or custom overrides would only break local setups).
 */
export function applyWorkspaceHost(url: string, workspace: string | Nullish): string;
export function applyWorkspaceHost(
  url: string | undefined,
  workspace: string | Nullish
): string | undefined;
export function applyWorkspaceHost(url: string | undefined, workspace: string | Nullish) {
  if (isNil(url) || isNil(workspace)) {
    return url;
  }

  const workspaceLabel = workspace.trim();
  if (
    workspaceLabel === '' ||
    workspaceLabel === appWorkspaceKey ||
    !WORKSPACE_LABEL_PATTERN.test(workspaceLabel)
  ) {
    return url;
  }

  const parsed = new URL(url);
  const [firstHostLabel] = parsed.hostname.split('.');
  if (parsed.hostname.startsWith(`${workspaceLabel}.`) || firstHostLabel !== API_HOST_FIRST_LABEL) {
    return url;
  }

  parsed.hostname = `${workspaceLabel}.${parsed.hostname}`;
  const result = parsed.toString();

  // URL#toString appends a trailing slash to a bare origin; keep the input shape
  if (!url.endsWith('/') && result.endsWith('/')) {
    return result.slice(0, -1);
  }

  return result;
}
